import { EllipsisHorizontalIcon } from "@heroicons/react/20/solid";
import { EyeIcon } from "@heroicons/react/24/outline";
import { Table, TableHead, TableRow, TableHeader, TableBody, TableCell } from "../../Catalyst/table";
import { Dropdown, DropdownButton, DropdownItem, DropdownLabel, DropdownMenu } from "../../Catalyst/dropdown";
import { USER_STATUS } from "../../common/UserListTable";
import { ROLE_DISPLAY } from "../GlobalSearch/utils";
import { formatStatusDate } from "./utils";

// Row shape of GET /master-data/users (v1, snake_case). Only the fields this
// table reads are typed; the endpoint returns more.
export interface OrgUser {
  id: string;
  first: string | null;
  last: string | null;
  email: string;
  user_role: string;
  user_status: string | null;
  user_status_date: string | null;
  // Not returned by the API today; rendered as "–" until it is (see ADM-5 notes).
  session_count?: number | null;
}

// Status text colour + dot per the design (green / grey / red; signed-up amber),
// mirroring the user-status palette used by the user list and global search.
const USER_STATUS_DISPLAY: Record<string, { label: string; textClass: string; dotClass: string }> = {
  [USER_STATUS.ACTIVE]: { label: "Active", textClass: "text-green-600", dotClass: "bg-green-500" },
  [USER_STATUS.INVITED]: { label: "Invited", textClass: "text-textColor", dotClass: "bg-gray-400" },
  [USER_STATUS.DEACTIVATED]: { label: "Deactivated", textClass: "text-red-500", dotClass: "bg-red-400" },
  [USER_STATUS.SIGNED_UP]: { label: "Signed Up", textClass: "text-yellow-600", dotClass: "bg-yellow-500" },
};

// "Active since June 5, 2026" / "Invited on June 5, 2026" / "Deactivated on …".
const getStatusLabel = (user: OrgUser) => {
  const status = user.user_status || USER_STATUS.ACTIVE;
  const config = USER_STATUS_DISPLAY[status] ?? USER_STATUS_DISPLAY[USER_STATUS.ACTIVE];
  const date = formatStatusDate(user.user_status_date);
  const preposition = status === USER_STATUS.ACTIVE ? "since" : "on";
  return { ...config, text: date ? `${config.label} ${preposition} ${date}` : config.label };
};

const getDisplayName = (user: OrgUser) =>
  [user.first, user.last].filter(Boolean).join(" ").trim() || user.email;

interface OrgUsersTableProps {
  users: OrgUser[];
  onViewActivity: (user: OrgUser) => void;
}

const OrgUsersTable = ({ users, onViewActivity }: OrgUsersTableProps) => {
  return (
    <Table>
      <TableHead>
        <TableRow className="bg-[#F9FAFB] border border-x-[#EAECF0]">
          <TableHeader className="w-[34%]">Name</TableHeader>
          <TableHeader className="w-[18%]">Role</TableHeader>
          <TableHeader className="w-[26%]">Status</TableHeader>
          <TableHeader className="w-[15%]">Number of Sessions</TableHeader>
          <TableHeader className="w-[7%] text-right">Actions</TableHeader>
        </TableRow>
      </TableHead>
      <TableBody>
        {users.map((user) => {
          const status = getStatusLabel(user);
          const isDimmed =
            user.user_status === USER_STATUS.DEACTIVATED || user.user_status === USER_STATUS.INVITED;
          return (
            <TableRow key={user.id}>
              <TableCell className={`font-medium text-texthead ${isDimmed ? "opacity-50" : ""}`}>
                {getDisplayName(user)}
              </TableCell>
              <TableCell className="text-texthead">
                {ROLE_DISPLAY[user.user_role] ?? user.user_role}
              </TableCell>
              <TableCell>
                <span className={`inline-flex items-center gap-2 text-sm font-medium ${status.textClass}`}>
                  {status.text}
                  <span className={`h-2 w-2 rounded-full ${status.dotClass}`} aria-hidden="true" />
                </span>
              </TableCell>
              <TableCell className="text-texthead">
                {typeof user.session_count === "number" ? user.session_count : "–"}
              </TableCell>
              <TableCell className="text-right">
                <Dropdown>
                  <DropdownButton outline aria-label={`Actions for ${getDisplayName(user)}`}>
                    <EllipsisHorizontalIcon />
                  </DropdownButton>
                  <DropdownMenu anchor="bottom end">
                    <DropdownItem onClick={() => onViewActivity(user)}>
                      <EyeIcon />
                      <DropdownLabel>View Activity</DropdownLabel>
                    </DropdownItem>
                  </DropdownMenu>
                </Dropdown>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};

export default OrgUsersTable;
